import { BAG_SECTIONS, describeItemEffect, questCategories, questCategoryOf, type Item, type ItemId } from '../../shared/items.ts';
import { elementTypes } from '../../shared/elements.ts';
import type { Move } from '../../shared/moves.ts';
import type { Inventory, OwnedMonsterView } from '../../shared/protocol.ts';
import { itemCatalog, itemVisual } from '../common/item-catalog.ts';
import { $, el } from '../common/dom.ts';
import { icon, withIcon } from '../common/icons.ts';
import { Modal } from '../common/modal.ts';
import { creatureText } from '../../shared/game-config.ts';

/** Monstre à qui l'on donne un objet depuis sa fiche. */
export interface GiveTarget {
  name: string;
  hp: number;
  maxHp: number;
}

/**
 * Sac à dos : les objets possédés, rangés par utilité (capture, soin, sur la carte, équipement, attaques, quête) ; on y
 * équipe ses équipements, on y utilise les objets « sur la carte » (Repousse…) et on y apprend les attaques des CT aux
 * {monstres} de l'équipe. Les objets de quête sont sous-rangés par catégorie (« Lettres »…). Un clic sur un objet ouvre sa
 * fiche (description complète). Ouvert depuis la fiche d'un monstre, il ne propose que les consommables à lui donner (potions).
 */
export class BagPanel extends Modal {
  private readonly body = $('.bag-body', this.root);
  private readonly subtitle = $('.modal-head p', this.root);
  private readonly defaultSubtitle = this.subtitle.textContent ?? '';
  private readonly onEquip: (item: ItemId, equipped: boolean) => void;
  private readonly onUse: (item: ItemId) => void;
  private readonly onTeach: (monster: OwnedMonsterView, moveId: number) => void;
  private inventory: Inventory | null = null;
  private team: OwnedMonsterView[] = [];
  private moves = new Map<number, Move>();
  /** Monstre à qui donner un objet (null : sac ouvert normalement). */
  private giving: { target: GiveTarget; onGive: (item: ItemId) => void } | null = null;

  constructor(onEquip: (item: ItemId, equipped: boolean) => void, onUse: (item: ItemId) => void, onTeach: (monster: OwnedMonsterView, moveId: number) => void) {
    super('#bag');
    this.onEquip = onEquip;
    this.onUse = onUse;
    this.onTeach = onTeach;
    $('#btn-bag').addEventListener('click', () => this.toggle());
  }

  protected override reveal(): void {
    return this.show();
  }

  show(): void {
    this.giving = null;
    this.open();
    this.render();
  }

  /** Ouvre le sac par-dessus la fiche d'un monstre, pour lui donner un consommable ; il se ferme une fois l'objet donné. */
  giveTo(target: GiveTarget, onGive: (item: ItemId) => void): void {
    this.giving = { target, onGive: (item) => { onGive(item); this.close(); } };
    this.open();
    this.render();
  }

  override close(): void {
    super.close();
    this.giving = null;
  }

  setInventory(inventory: Inventory): void {
    this.inventory = inventory;
    if (this.isOpen) this.render();
  }

  /** Équipe du joueur : ses {monstres} qui peuvent apprendre les attaques des CT. */
  setCollection(collection: readonly OwnedMonsterView[]): void {
    this.team = collection.filter((monster) => monster.slot !== null).sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0));
    if (this.isOpen) this.render();
  }

  setMoveCatalog(catalog: Map<number, Move>): void {
    this.moves = catalog;
    if (this.isOpen) this.render();
  }

  private render(): void {
    const { inventory, giving } = this;
    this.root.classList.toggle('giving', giving !== null);
    this.subtitle.textContent = giving ? `Choisissez l'objet à donner à ${giving.target.name}.` : this.defaultSubtitle;
    if (!inventory) {
      this.body.replaceChildren(el('p', { className: 'muted', textContent: 'Chargement…' }));
      return;
    }
    // En donnant à un monstre : seulement les consommables (les potions, pour l'instant).
    const owned = itemCatalog.all().filter((item) => (inventory.items[item.id] ?? 0) > 0 && (!giving || item.effect.type === 'heal'));
    if (!owned.length) {
      this.body.replaceChildren(el('p', { className: 'empty', textContent: giving ? 'Aucun objet à donner : la boutique vend des potions.' : 'Votre sac est vide.' }));
      return;
    }
    this.body.replaceChildren(...BAG_SECTIONS.flatMap(({ effect, label }) => {
      const items = owned.filter((item) => item.effect.type === effect);
      if (!items.length) return [];
      return [el('section', { className: 'bag-section' },
        el('h3', { textContent: label }),
        ...(effect === 'none' ? this.questGroups(items, inventory) : [this.list(items, inventory)]))];
    }));
  }

  private list(items: readonly Item[], inventory: Inventory): HTMLElement {
    return el('ul', { className: 'bag-items' }, ...items.map((item) => this.row(item, inventory)));
  }

  /** Objets de quête : une sous-rubrique par catégorie, puis ceux sans catégorie (« Autres », s'il y a des catégories). */
  private questGroups(items: readonly Item[], inventory: Inventory): HTMLElement[] {
    const categories = questCategories(items);
    if (!categories.length) return [this.list(items, inventory)];
    const loose = items.filter((item) => !questCategoryOf(item));
    return [
      ...categories.map((category) => el('div', { className: 'bag-subsection' },
        el('h4', { textContent: category }),
        this.list(items.filter((item) => questCategoryOf(item) === category), inventory))),
      ...(loose.length ? [el('div', { className: 'bag-subsection' }, el('h4', { textContent: 'Autres' }), this.list(loose, inventory))] : []),
    ];
  }

  private row(item: Item, inventory: Inventory): HTMLElement {
    const equipped = inventory.equipped.includes(item.id);
    const { effect } = item;
    const taught = effect.type === 'move' ? this.moves.get(effect.moveId) : undefined;
    const summary = taught ? `${taught.name} · type ${elementTypes.get(taught.element).label} · usage illimité` : describeItemEffect(effect);
    const line = el('li', { className: `bag-item ${equipped ? 'equipped' : ''}` },
      itemVisual(item, 'bag-visual'),
      el('div', { className: 'bag-text' },
        // Une CT ne s'use pas : son nombre ne compte pas.
        el('strong', { textContent: item.name }, effect.type === 'move' ? '' : el('span', { className: 'bag-count', textContent: ` ×${inventory.items[item.id] ?? 0}` })),
        el('small', { textContent: summary }),
        item.description ? el('p', { className: 'muted bag-description', textContent: creatureText(item.description) }) : ''));
    // Clic (ou Entrée) sur l'objet, hors de ses boutons : sa fiche, avec la description complète.
    line.classList.add('inspectable');
    line.tabIndex = 0;
    line.title = 'Voir la fiche de l\'objet';
    const inspect = () => showItemDetail(item, effect.type === 'move' ? null : inventory.items[item.id] ?? 0, summary);
    line.addEventListener('click', (event) => { if (!(event.target as Element).closest('button')) inspect(); });
    line.addEventListener('keydown', (event) => {
      if (event.target === line && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); inspect(); }
    });
    if (this.giving) {
      const { target, onGive } = this.giving;
      const reason = target.hp <= 0 ? 'K.O. : passez par une borne de soin.' : target.hp >= target.maxHp ? 'Déjà en pleine forme.' : '';
      const button = el('button', { type: 'button', disabled: reason !== '', title: reason }, ...withIcon('gift', 'Donner'));
      button.addEventListener('click', () => onGive(item.id));
      line.append(button);
    } else if (item.effect.type === 'field') {
      const button = el('button', { type: 'button' }, ...withIcon('check', 'Utiliser'));
      // La Corde de sortie emmène ailleurs : le sac se referme pour voir l'arrivée.
      button.addEventListener('click', () => { this.onUse(item.id); if (item.effect.type === 'field' && item.effect.action === 'escape') this.close(); });
      line.append(button);
    } else if (effect.type === 'move') {
      line.classList.add('teaching');
      line.append(this.learners(effect.moveId, taught));
    } else if (item.effect.type === 'equipment') {
      const button = el('button', { type: 'button', className: equipped ? 'ghost' : '' }, ...withIcon(equipped ? 'close' : 'check', equipped ? 'Retirer' : 'Équiper'));
      button.addEventListener('click', () => this.onEquip(item.id, !equipped));
      line.append(button);
    }
    return line;
  }

  /** Les {monstres} de l'équipe du type de l'attaque : un bouton chacun pour la lui apprendre. */
  private learners(moveId: number, move: Move | undefined): HTMLElement {
    const box = el('div', { className: 'bag-learners' });
    if (!move) return box;
    // Le serveur ajoute l'attaque des CT du sac à celles que peut apprendre chaque {monstre} du bon type.
    const able = this.team.filter((monster) => monster.learnable.includes(moveId));
    if (!able.length) {
      box.append(el('small', { className: 'muted', textContent: creatureText(`Aucun {monstre} de votre équipe n'est de type ${elementTypes.get(move.element).label}.`) }));
      return box;
    }
    box.append(el('small', { className: 'muted', textContent: 'Apprendre à :' }));
    for (const monster of able) {
      const knows = monster.moves.includes(moveId);
      const button = el('button', { type: 'button', className: 'secondary', disabled: knows, title: knows ? `${monster.name} connaît déjà ${move.name}.` : `Apprendre ${move.name} à ${monster.name}` },
        monster.name);
      button.addEventListener('click', () => this.onTeach(monster, moveId));
      box.append(button);
    }
    return box;
  }
}

/**
 * Fiche d'un objet, par-dessus le sac : grand visuel, nombre possédé (null : sans objet, comme pour une CT), utilité et
 * description complète. Échap, la croix ou un clic sur le fond la ferment ; la touche n'atteint ni le jeu ni le sac.
 */
function showItemDetail(item: Item, count: number | null, summary: string): void {
  const close = el('button', { className: 'icon modal-close', ariaLabel: 'Fermer' }, icon('close'));
  const dialog = el('section', { className: 'modal item-detail' },
    el('header', { className: 'item-detail-head' },
      itemVisual(item, 'item-detail-visual'),
      el('div', {},
        el('h2', { textContent: item.name }, count === null ? '' : el('span', { className: 'bag-count', textContent: ` ×${count}` })),
        summary ? el('small', { className: 'muted', textContent: summary }) : ''),
      close),
    el('p', { className: `item-detail-description ${item.description ? '' : 'muted'}`, textContent: item.description ? creatureText(item.description) : 'Aucune description.' }));
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-label', item.name);
  const overlay = el('div', { className: 'modal-overlay item-detail-overlay' }, dialog);
  const onKey = (event: KeyboardEvent) => {
    event.stopImmediatePropagation();
    if (event.key === 'Escape') finish();
  };
  const finish = () => {
    window.removeEventListener('keydown', onKey, true);
    overlay.remove();
  };
  close.addEventListener('click', finish);
  overlay.addEventListener('click', (event) => { if (event.target === overlay) finish(); });
  window.addEventListener('keydown', onKey, true);
  document.body.append(overlay);
  close.focus();
}
